import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";

const GiftManagement = () => {
  const [gifts, setGifts] = useState([]);
  const [giftImages, setGiftImages] = useState([]); // Danh sách hình ảnh quà tặng (bao gồm name và imageUrl)
  const [filteredImages, setFilteredImages] = useState([]); // Danh sách hình ảnh sau khi lọc tìm kiếm
  const [searchTerm, setSearchTerm] = useState(""); // Từ khóa tìm kiếm
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [newGift, setNewGift] = useState({ name: "", coinPrice: "", imageUrl: "" });
  const [editingGift, setEditingGift] = useState(null);
  const { fetchWithRetry } = useAuth();
  const imageInputRef = useRef(null);

  // Tải danh sách quà tặng
  const loadGifts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchWithRetry({ method: "get", url: "/api/admin/gifts" });
      setGifts(res.data);
      setLoading(false);
    } catch (error) {
      setError(error.response?.data?.message || "Không thể tải danh sách quà tặng!");
      setLoading(false);
    }
  }, [fetchWithRetry]);

  // Tải danh sách hình ảnh quà tặng
  const loadGiftImages = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/admin/gifts/images" });
      setGiftImages(res.data);
      setFilteredImages(res.data);
    } catch (error) {
      setError(error.response?.data?.message || "Không thể tải danh sách hình ảnh quà tặng!");
    }
  }, [fetchWithRetry]);

  // Tìm kiếm hình ảnh theo tên
  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = giftImages.filter((image) =>
      image.name.toLowerCase().includes(term)
    );
    setFilteredImages(filtered);
  };

  // Upload nhiều hình ảnh mới vào danh sách
  const handleUploadImages = async () => {
    try {
      if (!imageInputRef.current.files || imageInputRef.current.files.length === 0) {
        throw new Error("Vui lòng chọn ít nhất một hình ảnh để upload!");
      }
      const formData = new FormData();
      for (const file of imageInputRef.current.files) {
        formData.append("images", file);
      }

      const res = await fetchWithRetry({
        method: "post",
        url: "/api/admin/gifts/upload-image",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      const newImages = res.data.images; // Danh sách { name, imageUrl }
      setGiftImages((prev) => [
        ...prev,
        ...newImages.filter((newImage) => !prev.some((img) => img.imageUrl === newImage.imageUrl)),
      ]);
      setFilteredImages((prev) => [
        ...prev,
        ...newImages.filter((newImage) => !prev.some((img) => img.imageUrl === newImage.imageUrl)),
      ]);
      setSuccessMessage("Upload hình ảnh thành công!");
      imageInputRef.current.value = null;
    } catch (error) {
      setError(error.response?.data?.message || "Không thể upload hình ảnh!");
    }
  };

  // Xóa hình ảnh
  const handleDeleteImage = async (imageUrl) => {
    if (!window.confirm("Bạn có chắc muốn xóa hình ảnh này?")) return;
    try {
      const res = await fetchWithRetry({
        method: "delete",
        url: "/api/admin/gifts/delete-image",
        data: { imageUrl },
      });
      setGiftImages((prev) => prev.filter((img) => img.imageUrl !== imageUrl));
      setFilteredImages((prev) => prev.filter((img) => img.imageUrl !== imageUrl));
      setSuccessMessage(res.data.message);
    } catch (error) {
      setError(error.response?.data?.message || "Không thể xóa hình ảnh!");
    }
  };

  // Thêm quà tặng
  const handleAddGift = async () => {
    try {
      if (!newGift.imageUrl) {
        throw new Error("Vui lòng chọn một hình ảnh từ danh sách!");
      }
      const res = await fetchWithRetry({
        method: "post",
        url: "/api/admin/gifts",
        data: newGift,
      });
      setSuccessMessage(res.data.message);
      setNewGift({ name: "", coinPrice: "", imageUrl: "" });
      loadGifts();
    } catch (error) {
      setError(error.response?.data?.message || "Không thể thêm quà tặng!");
    }
  };

  // Cập nhật quà tặng
  const handleUpdateGift = async () => {
    try {
      const res = await fetchWithRetry({
        method: "put",
        url: `/api/admin/gifts/${editingGift._id}`,
        data: editingGift,
      });
      setSuccessMessage(res.data.message);
      setEditingGift(null);
      loadGifts();
    } catch (error) {
      setError(error.response?.data?.message || "Không thể cập nhật quà tặng!");
    }
  };

  // Xóa quà tặng
  const handleDeleteGift = async (id) => {
    if (!window.confirm("Bạn có chắc muốn xóa quà tặng này?")) return;
    try {
      const res = await fetchWithRetry({
        method: "delete",
        url: `/api/admin/gifts/${id}`,
      });
      setSuccessMessage(res.data.message);
      loadGifts();
    } catch (error) {
      setError(error.response?.data?.message || "Không thể xóa quà tặng!");
    }
  };

  useEffect(() => {
    loadGifts();
    loadGiftImages();
  }, [loadGifts, loadGiftImages]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h2 className="text-2xl font-bold mb-4">Quản lý quà tặng</h2>
      {error && <p className="text-red-500 mb-4">{error}</p>}
      {successMessage && <p className="text-green-500 mb-4">{successMessage}</p>}

      {/* Upload nhiều hình ảnh mới */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-2">Upload hình ảnh quà tặng</h3>
        <input
          type="file"
          ref={imageInputRef}
          accept="image/*"
          multiple
          className="border p-2 rounded mb-2 w-full"
        />
        <button
          onClick={handleUploadImages}
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
        >
          Upload
        </button>
      </div>

      {/* Danh sách hình ảnh quà tặng với tìm kiếm */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-2">Danh sách hình ảnh quà tặng</h3>
        <input
          type="text"
          placeholder="Tìm kiếm theo tên ảnh..."
          value={searchTerm}
          onChange={handleSearch}
          className="border p-2 rounded mb-4 w-full"
        />
        {filteredImages.length > 0 ? (
          <div className="grid grid-cols-5 gap-4">
            {filteredImages.map((image, index) => (
              <div key={index} className="flex flex-col items-center">
                <img
                  src={image.imageUrl}
                  alt={image.name}
                  className="w-16 h-16 object-cover mb-2"
                  onError={(e) => (e.target.src = "https://placehold.co/50x50")}
                />
                <p className="text-sm text-center mb-2">{image.name}</p>
                <button
                  onClick={() => handleDeleteImage(image.imageUrl)}
                  className="bg-red-500 text-white px-2 py-1 rounded"
                >
                  Xóa
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">Chưa có hình ảnh nào trong thư viện.</p>
        )}
      </div>

      {/* Form thêm quà tặng */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-2">Thêm quà tặng mới</h3>
        <input
          type="text"
          placeholder="Tên quà tặng"
          value={newGift.name}
          onChange={(e) => setNewGift({ ...newGift, name: e.target.value })}
          className="border p-2 rounded mb-2 w-full"
        />
        <input
          type="number"
          placeholder="Giá coin"
          value={newGift.coinPrice}
          onChange={(e) => setNewGift({ ...newGift, coinPrice: e.target.value })}
          className="border p-2 rounded mb-2 w-full"
        />
        <div className="mb-4">
          <h4 className="text-sm font-medium mb-2">Chọn hình ảnh</h4>
          {filteredImages.map((image, index) => (
            <div key={index} className="flex items-center mb-2">
              <input
                type="radio"
                name="giftImage"
                value={image.imageUrl}
                checked={newGift.imageUrl === image.imageUrl}
                onChange={(e) => setNewGift({ ...newGift, imageUrl: e.target.value })}
                className="mr-2"
              />
              <img
                src={image.imageUrl}
                alt={image.name}
                className="w-8 h-8 object-cover mr-2"
                onError={(e) => (e.target.src = "https://placehold.co/50x50")}
              />
              <span>{image.name}</span>
            </div>
          ))}
        </div>
        <button
          onClick={handleAddGift}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Thêm
        </button>
      </div>

      {/* Danh sách quà tặng */}
      {loading ? (
        <p>Đang tải...</p>
      ) : (
        <table className="w-full bg-white rounded-lg shadow">
          <thead>
            <tr className="bg-blue-500 text-white">
              <th className="p-2">Hình ảnh</th>
              <th className="p-2">Tên</th>
              <th className="p-2">Giá coin</th>
              <th className="p-2">Hành động</th>
            </tr>
          </thead>
          <tbody>
            {gifts.map((gift) => (
              <tr key={gift._id} className="border-b">
                <td className="p-2">
                  <img
                    src={gift.imageUrl}
                    alt={gift.name}
                    className="w-12 h-12 object-cover"
                    onError={(e) => (e.target.src = "https://placehold.co/50x50")}
                  />
                </td>
                <td className="p-2">{gift.name}</td>
                <td className="p-2">{gift.coinPrice}</td>
                <td className="p-2">
                  <button
                    onClick={() => setEditingGift(gift)}
                    className="bg-yellow-500 text-white px-2 py-1 rounded mr-2"
                  >
                    Sửa
                  </button>
                  <button
                    onClick={() => handleDeleteGift(gift._id)}
                    className="bg-red-500 text-white px-2 py-1 rounded"
                  >
                    Xóa
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modal chỉnh sửa quà tặng */}
      {editingGift && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow w-96">
            <h3 className="text-lg font-semibold mb-2">Chỉnh sửa quà tặng</h3>
            <input
              type="text"
              value={editingGift.name}
              onChange={(e) => setEditingGift({ ...editingGift, name: e.target.value })}
              className="border p-2 rounded mb-2 w-full"
            />
            <input
              type="number"
              value={editingGift.coinPrice}
              onChange={(e) => setEditingGift({ ...editingGift, coinPrice: e.target.value })}
              className="border p-2 rounded mb-2 w-full"
            />
            <div className="mb-4">
              <h4 className="text-sm font-medium mb-2">Chọn hình ảnh</h4>
              {filteredImages.map((image, index) => (
                <div key={index} className="flex items-center mb-2">
                  <input
                    type="radio"
                    name="editGiftImage"
                    value={image.imageUrl}
                    checked={editingGift.imageUrl === image.imageUrl}
                    onChange={(e) => setEditingGift({ ...editingGift, imageUrl: e.target.value })}
                    className="mr-2"
                  />
                  <img
                    src={image.imageUrl}
                    alt={image.name}
                    className="w-8 h-8 object-cover mr-2"
                    onError={(e) => (e.target.src = "https://placehold.co/50x50")}
                  />
                  <span>{image.name}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setEditingGift(null)}
                className="bg-gray-500 text-white px-4 py-2 rounded mr-2"
              >
                Hủy
              </button>
              <button
                onClick={handleUpdateGift}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Cập nhật
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftManagement;